import {
  Alert, Anchor, Badge, Box, Button, Card, Divider, Grid, Group, List, NavLink, Paper, RingProgress, Select, Stack, Text, ThemeIcon, Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconArrowLeft, IconArrowRight, IconBan, IconCheck, IconCircle, IconCircleCheckFilled, IconClipboardCheck, IconClock,
  IconLock, IconPlayerPlay, IconRefresh, IconTarget,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api } from '../../lib/api';
import { DONE, dueLabel, fmtDate, fmtDateTime, score } from '../../lib/format';
import type { CourseDetail } from '../../lib/types';
import { AttachmentBlock, VideoBlock } from '../../ui/LessonMedia';
import { Markdown } from '../../ui/Markdown';
import { QueryState } from '../../ui/QueryState';
import { StatusBadge } from '../../ui/StatusBadge';

function QuizBlock({ d }: { d: CourseDetail }) {
  const navigate = useNavigate();
  const lessonTitle = new Map(d.lessons.map((l) => [l.id, l.title]));
  const last = d.attempts.at(-1);
  const done = DONE.includes(d.status);

  return (
    <Card withBorder radius="lg" padding="lg">
      <Stack gap="sm">
        <Group gap="sm">
          <ThemeIcon variant="light" size="lg" radius="md" color={done ? 'teal' : 'blue'}><IconClipboardCheck size={20} /></ThemeIcon>
          <div>
            <Title order={4}>Итоговый тест</Title>
            <Text size="sm" c="dimmed">{d.questionCount} вопросов из пула курса · сдан, если верно не меньше {d.passCount} из {d.questionCount} · попыток: {d.attemptsLimit}</Text>
          </div>
        </Group>

        {done && (
          <Alert color="teal" icon={<IconCircleCheckFilled />} title={`Курс завершён ${fmtDate(d.completedAt)}`}>
            Итог: <b>{score(d.passedCorrect, d.questionCount)}</b>.{d.status === 'COMPLETED_LATE' && ' Курс пройден после срока.'} Полный разбор открыт в результатах попыток.
          </Alert>
        )}
        {d.status === 'CANCELLED' && <Alert color="gray" icon={<IconBan />} title="Назначение отменено">Проходить курс по этому назначению больше не нужно.</Alert>}
        {d.status === 'BLOCKED' && (
          <Alert color="red" icon={<IconLock />} title="Попытки закончились">
            Все {d.attemptsLimit} попытки использованы, полный разбор последней попытки открыт ниже. Новые попытки добавляет ответственный за назначения в команде обучения.
          </Alert>
        )}
        {d.status === 'RETAKE' && last && (
          <Alert color="orange" icon={<IconRefresh />} title={`Попытка ${last.attemptNo}: ${score(last.correctCount, last.questionCount)} - тест не сдан`}>
            <Text size="sm" mb={d.repeatLessonIds.length ? 6 : 0}>Осталось попыток: {d.attemptsLeft}. Следующая попытка соберётся из пула заново.</Text>
            {d.repeatLessonIds.length > 0 && (
              <>
                <Text size="sm" fw={500}>Повторите уроки:</Text>
                <List size="sm" mt={2}>
                  {d.repeatLessonIds.map((id) => <List.Item key={id}><Anchor component={Link} to={`/learn/${d.id}/lesson/${id}`}>{lessonTitle.get(id)}</Anchor></List.Item>)}
                </List>
              </>
            )}
          </Alert>
        )}
        {d.quiz.reasonCode === 'LESSONS' && (
          <Text size="sm" c="dimmed"><IconLock size={14} style={{ verticalAlign: -2 }} /> Тест откроется после отметки «Изучено» во всех обязательных уроках (осталось {d.quiz.missingLessonIds.length}).</Text>
        )}
        {(d.quiz.canStart || d.openAttemptId) && (
          <Button size="md" leftSection={<IconPlayerPlay size={18} />} onClick={() => navigate(`/learn/${d.id}/quiz`)}>
            {d.openAttemptId ? 'Продолжить тест с того же места' : d.attempts.length ? `Пересдать (попытка ${d.attempts.length + 1})` : 'Начать тест'}
          </Button>
        )}

        {d.attempts.length > 0 && (
          <>
            <Divider label="Попытки" labelPosition="left" />
            <Stack gap={6}>
              {d.attempts.map((t) => (
                <Group key={t.id} justify="space-between" wrap="nowrap">
                  <Group gap="xs" wrap="nowrap">
                    <Badge color={t.passed ? 'teal' : 'red'} variant="light">{score(t.correctCount, t.questionCount)}</Badge>
                    <Text size="sm">Попытка {t.attemptNo}</Text>
                  </Group>
                  <Group gap="xs" wrap="nowrap">
                    <Text size="xs" c="dimmed" visibleFrom="xs">{fmtDateTime(t.submittedAt)}</Text>
                    <Anchor component={Link} to={`/results/${t.id}`} size="sm">Результат</Anchor>
                  </Group>
                </Group>
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </Card>
  );
}

function Toc({ d, lessonId }: { d: CourseDetail; lessonId: number | null }) {
  const repeat = new Set(d.repeatLessonIds);
  return (
    <Card withBorder radius="lg" p="xs">
      <NavLink component={Link} to={`/learn/${d.id}`} label="О курсе" active={lessonId == null} style={{ borderRadius: 8 }} />
      {d.lessons.map((l, i) => (
        <NavLink
          key={l.id} component={Link} to={`/learn/${d.id}/lesson/${l.id}`} active={l.id === lessonId} style={{ borderRadius: 8 }}
          leftSection={l.completedAt ? <IconCircleCheckFilled size={18} color="var(--mantine-color-teal-6)" /> : <IconCircle size={18} color="var(--mantine-color-gray-5)" />}
          label={<Text size="sm">{i + 1}. {l.title}</Text>}
          rightSection={repeat.has(l.id) && d.status === 'RETAKE' ? <Badge size="xs" color="orange" variant="light">повторить</Badge> : null}
        />
      ))}
      <NavLink component={Link} to={`/learn/${d.id}#quiz`} style={{ borderRadius: 8 }} leftSection={<IconTarget size={18} color="var(--mantine-color-blue-6)" />} label={<Text size="sm" fw={500}>Итоговый тест</Text>} />
    </Card>
  );
}

function Overview({ d }: { d: CourseDetail }) {
  const navigate = useNavigate();
  const next = d.lessons.find((l) => !l.completedAt) ?? d.lessons[0];
  const done = DONE.includes(d.status);
  const due = dueLabel(d.dueAt, done || d.status === 'CANCELLED');
  const pct = d.lessonsTotal ? Math.round((d.lessonsDone / d.lessonsTotal) * 100) : 0;
  useEffect(() => {
    if (location.hash === '#quiz') document.getElementById('quiz')?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  return (
    <Stack>
      <Card withBorder radius="lg" padding="lg">
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="lg">
          <Stack gap="xs" style={{ flex: 1 }}>
            <Group gap="xs"><Badge variant="dot" color="gray">{d.category}</Badge><StatusBadge status={d.status} /></Group>
            <Title order={2}>{d.title}</Title>
            <Text c="dimmed">{d.description}</Text>
            <Group gap="lg" mt={4}>
              <Group gap={4}><IconClock size={16} color="gray" /><Text size="sm">~{d.durationMin} мин</Text></Group>
              <Text size="sm" c={due.color}>{due.text}</Text>
              <Text size="sm" c="dimmed">Версия курса {d.version}</Text>
            </Group>
          </Stack>
          <RingProgress visibleFrom="xs" size={96} thickness={9} roundCaps sections={[{ value: pct, color: done ? 'teal' : 'blue' }]} label={<Text ta="center" size="sm" fw={700}>{d.lessonsDone}/{d.lessonsTotal}</Text>} />
        </Group>
        {!done && d.status !== 'CANCELLED' && next && (
          <Button mt="md" rightSection={<IconArrowRight size={16} />} onClick={() => navigate(`/learn/${d.id}/lesson/${next.id}`)}>
            {d.lessonsDone === 0 ? 'Начать обучение' : d.status === 'RETAKE' ? 'Повторить материал' : 'Продолжить'}
          </Button>
        )}
      </Card>
      <div id="quiz"><QuizBlock d={d} /></div>
    </Stack>
  );
}

function LessonView({ d, lessonId }: { d: CourseDetail; lessonId: number }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const idx = d.lessons.findIndex((l) => l.id === lessonId);
  const lesson = d.lessons[idx];
  const prev = d.lessons[idx - 1];
  const next = d.lessons[idx + 1];
  const complete = useMutation({
    mutationFn: () => api.post(`/me/courses/${d.id}/lessons/${lessonId}/complete`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['course', d.id] });
      await qc.invalidateQueries({ queryKey: ['my-courses'] });
      navigate(next ? `/learn/${d.id}/lesson/${next.id}` : `/learn/${d.id}#quiz`);
      window.scrollTo({ top: 0 });
    },
    onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
  });

  if (!lesson) return <Alert color="red">Урок не найден</Alert>;
  const canMark = !DONE.includes(d.status) && d.status !== 'CANCELLED';
  return (
    <Stack>
      <Card withBorder radius="lg" padding="xl">
        <Group justify="space-between" mb="xs">
          <Text size="sm" c="dimmed">Урок {idx + 1} из {d.lessons.length}</Text>
          {lesson.completedAt && <Badge color="teal" variant="light" leftSection={<IconCheck size={12} />}>Изучено</Badge>}
        </Group>
        <Title order={2} mb="lg">{lesson.title}</Title>
        <Stack gap="lg">
          {lesson.videoUrl && <VideoBlock url={lesson.videoUrl} />}
          {lesson.content && <Markdown attachmentPath={(id) => `/me/courses/${d.id}/files/${id}`}>{lesson.content}</Markdown>}
          {lesson.file && <AttachmentBlock path={`/me/courses/${d.id}/files/${lesson.file.id}`} filename={lesson.file.filename} mime={lesson.file.mime} />}
          {lesson.checklist.length > 0 && (
            <Paper radius="md" p="md" bg="var(--mantine-color-blue-light)">
              <Text fw={600} mb="xs">Что запомнить</Text>
              <List spacing={6} icon={<ThemeIcon size={20} radius="xl" color="blue"><IconCheck size={13} /></ThemeIcon>}>
                {lesson.checklist.map((c, i) => <List.Item key={i}><Text size="sm">{c}</Text></List.Item>)}
              </List>
            </Paper>
          )}
        </Stack>
      </Card>
      <Group justify="space-between">
        <Button variant="default" leftSection={<IconArrowLeft size={16} />} component={Link} to={prev ? `/learn/${d.id}/lesson/${prev.id}` : `/learn/${d.id}`}>{prev ? 'Назад' : 'О курсе'}</Button>
        {lesson.completedAt || !canMark ? (
          <Button variant="light" rightSection={<IconArrowRight size={16} />} component={Link} to={next ? `/learn/${d.id}/lesson/${next.id}` : `/learn/${d.id}#quiz`}>{next ? 'Следующий урок' : 'К тесту'}</Button>
        ) : (
          <Button color="teal" leftSection={<IconCheck size={16} />} loading={complete.isPending} onClick={() => complete.mutate()}>Изучено{next ? ', дальше' : ', к тесту'}</Button>
        )}
      </Group>
      {canMark && !lesson.completedAt && <Text size="xs" c="dimmed" ta="right">«Изучено» - ваша отметка, по ней открывается тест.</Text>}
    </Stack>
  );
}

export function CourseViewPage() {
  const params = useParams();
  const assignmentId = Number(params.assignmentId);
  const lessonId = params.lessonId ? Number(params.lessonId) : null;
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['course', assignmentId], queryFn: () => api.get<CourseDetail>(`/me/courses/${assignmentId}`) });

  return (
    <QueryState loading={q.isLoading} error={q.error}>
      {() => {
        const d = q.data!;
        return (
          <Stack>
            <Group gap="xs">
              <Anchor component={Link} to="/" size="sm"><IconArrowLeft size={14} style={{ verticalAlign: -2 }} /> Мои курсы</Anchor>
              {d.status === 'OVERDUE' && <Badge color="red" variant="light" leftSection={<IconAlertTriangle size={12} />}>Срок прошёл {fmtDate(d.dueAt)}</Badge>}
            </Group>
            <Box hiddenFrom="md">
              <Select
                aria-label="Раздел курса"
                value={lessonId ? String(lessonId) : 'overview'}
                onChange={(v) => navigate(v === 'overview' ? `/learn/${d.id}` : `/learn/${d.id}/lesson/${v}`)}
                data={[{ value: 'overview', label: 'О курсе и тест' }, ...d.lessons.map((l, i) => ({ value: String(l.id), label: `${l.completedAt ? '✓ ' : ''}${i + 1}. ${l.title}` }))]}
                allowDeselect={false}
              />
            </Box>
            <Grid gap="lg">
              <Grid.Col span={{ base: 12, md: 3.6 }} visibleFrom="md"><Box pos="sticky" top={76}><Toc d={d} lessonId={lessonId} /></Box></Grid.Col>
              <Grid.Col span={{ base: 12, md: 8.4 }}>{lessonId ? <LessonView d={d} lessonId={lessonId} /> : <Overview d={d} />}</Grid.Col>
            </Grid>
          </Stack>
        );
      }}
    </QueryState>
  );
}
